<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ProgressBar from 'primevue/progressbar';
import RadioButton from 'primevue/radiobutton';
import { useAttachmentStore } from '@/stores/attachment.js';
import { useToast } from "primevue/usetoast";

const props = defineProps({
  visible: Boolean,
  target: { type: String, required: true },
  entityId: { type: [Number, String], required: true },
  kind: { type: String, default: 'document' },
});
const emit = defineEmits(['update:visible', 'saved']);

const store = useAttachmentStore();
const toast = useToast();
const fileInput = ref(null); // ref для скрытого input

const documentTypes = ref([]);
const selectedType = ref(null);
const selectedFile = ref(null);
const fileName = ref('');
const loading = ref(false);
const uploadProgress = ref(0);
const isDragging = ref(false);
const dialogHeader = computed(() => props.kind === 'document' ? 'Добавить документ' : 'Добавить файл');


const documentTypeOptions = computed(() =>
    documentTypes.value.map(dt => ({ label: dt.item_name, value: dt.id }))
);

watch(() => props.visible, async (isVisible) => {
  if (isVisible) {
    resetForm();
    loading.value = true;
    try {
      documentTypes.value = await store.fetchDocumentTypes({ target: props.target, kind: props.kind });
    } catch (e) {
      console.error('DocumentForm failed to load types:', e);
      toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить типы документов', life: 3000 });
    } finally {
      loading.value = false;
    }
  }
});

function resetForm() {
  selectedFile.value = null;
  selectedType.value = null;
  fileName.value = '';
  uploadProgress.value = 0;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function formatSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function triggerFileSelect() {
  if(!loading.value) {
    fileInput.value.click();
  }
}

function onFileSelected(event) {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    fileName.value = file.name;
    uploadProgress.value = 0;
  }
}
function onFileDrop(event) {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file) {
    selectedFile.value = file;
    fileName.value = file.name;
    uploadProgress.value = 0;
  }
}

async function handleSubmit() {
  const finalFileName = fileName.value.trim();
  if (!selectedFile.value || !finalFileName || (props.kind === 'document' && !selectedType.value)) {
    toast.add({ severity: 'warn', summary: 'Внимание', detail: 'Выберите файл, тип и укажите имя файла', life: 3000 });
    return;
  }
  loading.value = true;
  uploadProgress.value = 0;

  const onUploadProgress = (progressEvent) => {
    if (progressEvent.total) {
      uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    }
  };

  try {
    await store.uploadAttachment({
      target: props.target,
      id: props.entityId,
      file: selectedFile.value,
      documentTypeId: selectedType.value,
      fileName: finalFileName,
      onUploadProgress,
      kind: props.kind,
    });
    const successMessage = props.kind === 'document' ? 'Документ успешно загружен' : 'Файл успешно загружен';
    toast.add({ severity: 'success', summary: 'Успех', detail: successMessage, life: 2500 });
    emit('saved');
    closeDialog();
  } catch (error) {
    const errorMessage = props.kind === 'document' ? 'Не удалось загрузить документ' : 'Не удалось загрузить файл';
    toast.add({ severity: 'error', summary: 'Ошибка загрузки', detail: error.message || errorMessage, life: 4000 });
    uploadProgress.value = 0;
  } finally {
    loading.value = false;
  }
}

function closeDialog() {
  emit('update:visible', false);
}
</script>

<template>
  <Dialog
      :visible="visible"
      @update:visible="closeDialog"
      modal
      :header="dialogHeader"
      :style="{ width: '650px' }"
  >
    <div class="flex flex-col gap-6">
      <!-- Кастомная область для загрузки -->
      <div v-if="!selectedFile">
        <div
            class="flex items-center justify-center flex-col py-4 cursor-pointer border-2 border-dashed  rounded-md transition-colors"
            :class="{
                'border-primary-500 bg-primary-50': isDragging,
                'border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800': !isDragging
            }"
            @click="triggerFileSelect"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onFileDrop"
        >
          <i class="pi pi-cloud-upload border-2 rounded-full p-4 text-4xl text-surface-400 dark:text-surface-500" />
          <p class="mt-3 mb-0 text-muted-color">Перетащите файл или нажмите для выбора.</p>
          <input type="file" ref="fileInput" @change="onFileSelected" class="hidden" />
        </div>
      </div>

      <!-- Отображение выбранного файла -->
      <div v-else class="p-4 border border-surface-200 dark:border-surface-700 rounded-md">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <i class="pi pi-file text-xl"></i>
            <span class="font-bold text-ellipsis max-w-sm whitespace-nowrap overflow-hidden">{{ selectedFile.name }}</span>
            <span class="text-muted-color">({{ formatSize(selectedFile.size) }})</span>
          </div>
          <Button icon="pi pi-times" variant="outlined" rounded severity="danger" @click="resetForm" :disabled="loading"/>
        </div>
        <ProgressBar :value="uploadProgress" class="mt-4 !h-2" :show-value="false" v-if="loading"></ProgressBar>
      </div>

      <!-- Поля формы -->
      <InputText v-model="fileName" placeholder="Имя файла (до 50 симв.)" maxlength="50" class="w-full" :disabled="!selectedFile || loading" />

      <div v-if="documentTypeOptions.length > 0">
        <p class="mb-3 font-semibold" :class="{'text-surface-500 dark:text-surface-400': !selectedFile || loading}">Тип {{ kind === 'document' ? 'документа' : 'файла' }}</p>
        <div class="flex flex-col gap-3">
          <div v-for="docType in documentTypeOptions" :key="docType.value" class="flex items-center">
            <RadioButton v-model="selectedType" :inputId="`type-${docType.value}`" name="docType" :value="docType.value" :disabled="!selectedFile || loading" />
            <label :for="`type-${docType.value}`" class="ml-2" :class="{'text-surface-500 dark:text-surface-400': !selectedFile || loading, 'cursor-pointer': selectedFile}">{{ docType.label }}</label>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-center gap-4 pt-2 w-full">
        <Button label="Сохранить" type="button" severity="info" rounded @click="handleSubmit" :loading="loading" :disabled="!selectedFile || (kind === 'document' && !selectedType) || !fileName" />

        <Button label="Отмена" type="button" severity="secondary" rounded variant="outlined" @click="closeDialog" :disabled="loading" />
      </div>
    </template>
  </Dialog>
</template>